import {gql} from "@apollo/client";

export const GET_PICKUP_APPOINTMENT_LISTING = gql`
    query GetPickupAppointmentListing(
        $filters: PickupAppointmentFiltersTypeInput
        $pageRequest: PageRequestTypeInput
    ) {
        getPickupAppointmentListing(filters: $filters, pageRequest: $pageRequest) {
            content {
                id
                appointmentNumber
                warehouseId
                warehouseName
                channelId
                channelName 
                cooperationWarehouseId
                cooperationWarehouseName
                pickupSiteId
                contactName
                addressLine1
                addressLine2
                contactPhone
                countryCode
                ext
                zipcode
                expectedPickupDate
                expectedPickupTimeStart
                expectedPickupTimeEnd
                timezone
                expectedParcelCount
                pickupOrderTotalCount
                actualParcelCount
                actualScanParcelCount
                scanTrackingNumber
                scanOrderId
                expectedParcelUnit
                actualParcelUnit
                notes
                trackingNumbersJson
                status
                exceptionReasonCode
                isSystem
                taskId
                taskNumber
                createdAt
                updatedAt
                source
                isNew
                continuousFailure
                confirm
                warehouseLatitude
                warehouseLongitude
                cooperationWarehouseLatitude
                cooperationWarehouseLongitude
                stopNumber
                stopNumberSeq
                driverName
                useVehicleModelId
                vehicleTypeId
                driverAccount
            }
            page
            size
            totalElements
            totalPages
            hasNext
            hasPrevious
        }
    }
`;

export interface PickupAppointmentListingResponse {
    getPickupAppointmentListing: PickupAppointmentListingResponseType;
}

/**
 * 揽收预约单分页响应类型
 */
export interface PickupAppointmentListingResponseType {
    content: PickupAppointmentType[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}


/**
 * 揽收预约单类型定义
 */
export interface PickupAppointmentType {
    id: number;
    appointmentNumber: string;
    warehouseId: number;
    warehouseName: string;
    channelId: number;
    channelName: string;
    cooperationWarehouseId?: number;
    cooperationWarehouseName?: string;
    pickupSiteId?: number;
    contactName?: string;
    addressLine1: string;
    addressLine2?: string;
    contactPhone?: string;
    countryCode?: number;
    ext?: string;
    zipcode: string;
    expectedPickupDate: string; // LocalDate 字符串格式
    expectedPickupTimeStart: string; // LocalTime 字符串格式
    expectedPickupTimeEnd: string; // LocalTime 字符串格式
    timezone: string;
    expectedParcelCount: number;
    /** 挂载运单数，供「预计揽收包裹」列使用，不受手动编辑影响 */
    pickupOrderTotalCount: number;
    actualParcelCount: number;
    actualScanParcelCount: number;
    /** 上门扫描单号：最早一条上门扫描记录的运单号；无扫描记录或开关未开时为空 */
    scanTrackingNumber?: string | null;
    /** 上门扫描单号对应的订单 ID，打开包裹详情用 */
    scanOrderId?: number | null;
    expectedParcelUnit?: string;
    actualParcelUnit?: string;
    notes?: string;
    trackingNumbersJson?: string[]; // 追踪号列表
    status?: string;
    exceptionReasonCode?: string;
    isSystem?: boolean; // 是否系统生成
    taskId: number;
    taskNumber: string;
    createdAt: string; // ISO 8601 时间戳格式
    updatedAt: string; // ISO 8601 时间戳格式
    source: string;
    isNew?: boolean; // 是否新仓
    continuousFailure?: boolean; // 是否连续失败
    confirm?: boolean; // 是否确认
    warehouseLatitude?: number; //hub 纬度
    warehouseLongitude?: number; //hub 经度
    cooperationWarehouseLatitude?: number; //合作仓库纬度
    cooperationWarehouseLongitude?: number; //合作仓库经度
    stopNumber?: number; //任务路径编号
    stopNumberSeq?: number; //任务路径编号序号
    driverName?: string; //揽收任务司机姓
    useVehicleModelId?: number; //站点使用车辆类型ID
    vehicleTypeId?: number; //车型ID：单上有值用单上的，为空时后端已回退到站点当日排期车型
    driverAccount?:number //揽收任务司机账号
}

/**
 * 揽收预约单过滤条件类型
 * 注意：warehouseId 不在 filters 中维护，而是通过 hook 参数传递
 */
export interface PickupAppointmentFiltersType {
    channelId?: number;
    cooperationWarehouseId?: number;
    cooperationWarehouseName?: string; // 前端专用：合作仓库名称模糊搜索
    routeId?: number; // 新增：路线ID（单选）
    routeIdList?: number[]; // 新增：路线ID列表（多选）
    zipcode?: string; // 邮编（单选）
    zipcodeList?: string[]; // 新增：邮编列表（多选）
    address?: string; // 新增：地址
    contactPhone?: string; // 联系电话
    status?: string;
    confirm?: number; // 0: pending confirmation, 1: confirmed
    source?: string; // STANDING_PICKUP/MANUAL/SYSTEM_SLA/SYSTEM/COPY（保留用于兼容上线）
    sourceList?: string[]; // 来源多选，查询时优先使用
    expectedParcelCount?: number;
    expectedParcelUnit?: string; // 改为字符串以接受枚举名称
    expectedPickupDate?: string; // LocalDate 字符串格式：YYYY-MM-DD
    appointmentNumberList?: string[]; // 预约单号列表
    isNew?: boolean; // 是否新仓
    continuousFailure?: boolean; // 是否连续失败
    pickupSerialNo?: string; // 提单流水号
    vehicleTypeId?: number; // 车辆类型ID（单选）
    vehicleTypeIdList?: number[]; // 车辆类型多选，查询时优先使用
    failureReasonCode?: string; // 揽收失败原因代码
    trackingNumber?: string; // 运单号
    driverId?: number; //揽收司机 ID
    cooperationWarehouseIdList?: number[]; // 站点ID多选
    statusList?: string[]; // 状态多选，与 status 并存时优先
    cityList?: string[]; // 城市多选，JOIN cw.address_core_city IN(...)
}


export const PickupAppointmentInitialValue: PickupAppointmentFiltersType = {
    channelId: undefined,
    zipcode: undefined,
    zipcodeList: undefined, // 新增：邮编列表（多选）
    address: undefined, // 新增：地址
    contactPhone: undefined,
    routeId: undefined, // 新增：路线ID（单选）
    routeIdList: undefined, // 新增：路线ID列表（多选）
    status: undefined,
    confirm: undefined,
    source: undefined,
    sourceList: undefined, // 来源：多选
    expectedParcelCount: undefined,
    expectedParcelUnit: undefined,
    expectedPickupDate: undefined, // 初始值为空，在页面初始化时动态设置为今天
    cooperationWarehouseId: undefined,
    cooperationWarehouseName: undefined, // 前端专用：合作仓库名称模糊搜索
    appointmentNumberList: undefined,
    isNew: undefined,// 是否新仓
    continuousFailure: undefined,// 是否连续失败
    pickupSerialNo: undefined, //提货单流水号
    vehicleTypeId: undefined, //车辆类型ID（单选）
    vehicleTypeIdList: undefined, //车辆类型多选
    failureReasonCode: undefined, //揽收失败原因代码
    trackingNumber: undefined, // 运单号
    driverId: undefined, //揽收司机 ID
    cooperationWarehouseIdList: undefined,
    statusList: undefined,
    cityList: undefined,
};